'use client'

import { useState } from 'react'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { toast } from 'sonner'
import { createClient } from '@/lib/supabase/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2, Eye, EyeOff, Package2, Users2, CalendarDays } from 'lucide-react'
import { SUSY } from '@/lib/constants'

const FEATURES = [
  { icon: Package2,     label: 'Prodotti & Listini',  sub: 'Catalogo, canali, scaglioni quantità' },
  { icon: Users2,       label: 'Creators & CRM',      sub: 'Referral, commissioni, pipeline vendita' },
  { icon: CalendarDays, label: 'Eventi',              sub: 'Fuorisalone, Zoomark, invitati per categoria' },
]

export default function LoginPage() {
  const router = useRouter()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({})

  function validate() {
    const e: typeof errors = {}
    if (!email) e.email = 'Email obbligatoria'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Formato email non valido'
    if (!password) e.password = 'Password obbligatoria'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return
    setLoading(true)
    setErrors({})

    const supabase = createClient()
    const { error } = await supabase.auth.signInWithPassword({ email, password })

    if (error) {
      if (error.message.includes('Invalid login credentials')) {
        setErrors({ password: 'Email o password non corretti' })
      } else {
        toast.error('Accesso fallito', { description: error.message })
      }
      setLoading(false)
      return
    }

    router.push('/cruscotto')
    router.refresh()
  }

  return (
    <div className="min-h-screen flex">
      {/* Pannello sinistro — brand */}
      <div
        className="hidden lg:flex lg:w-[45%] flex-col justify-between p-10 xl:p-14"
        style={{ backgroundColor: 'var(--susy-verde-bosco)' }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center overflow-hidden"
            style={{ backgroundColor: 'var(--susy-beige-fieno)' }}
          >
            <Image src="/susy-logo.png" alt="Susy®" width={38} height={38} className="object-contain" />
          </div>
          <div>
            <div className="text-base font-semibold font-display" style={{ color: 'var(--susy-beige-fieno)' }}>
              {SUSY.brand}
            </div>
            <div className="text-xs" style={{ color: 'rgba(245, 232, 219, 0.55)' }}>
              {SUSY.payoff}
            </div>
          </div>
        </div>

        <div>
          <h2 className="font-display text-4xl xl:text-5xl leading-[1.05] tracking-tight mb-4" style={{ color: 'var(--susy-beige-fieno)' }}>
            Il tuo animale<br />domestico è <em style={{ color: 'var(--susy-lino-pure)', fontStyle: 'italic' }}>casa</em>.
          </h2>
          <p className="text-base leading-relaxed mb-10 max-w-[400px]" style={{ color: 'rgba(245, 232, 219, 0.65)' }}>
            Hub operativo di Susy® — prodotti, listini, creators, CRM ed eventi.
            Tutto il brand in un unico posto.
          </p>

          <div className="space-y-3">
            {FEATURES.map(({ icon: Icon, label, sub }) => (
              <div
                key={label}
                className="flex items-center gap-4 p-4 rounded-xl"
                style={{ backgroundColor: 'rgba(245, 232, 219, 0.08)' }}
              >
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'rgba(245, 232, 219, 0.18)' }}
                >
                  <Icon className="w-4 h-4" style={{ color: 'var(--susy-beige-fieno)' }} />
                </div>
                <div>
                  <div className="text-sm font-medium" style={{ color: 'var(--susy-beige-fieno)' }}>{label}</div>
                  <div className="text-xs mt-0.5" style={{ color: 'rgba(245, 232, 219, 0.55)' }}>{sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-xs" style={{ color: 'rgba(245, 232, 219, 0.35)' }}>
          © {new Date().getFullYear()} Susy® di {SUSY.legalEntity} · P.IVA {SUSY.vat}
        </div>
      </div>

      {/* Pannello destro — form */}
      <div className="flex-1 flex items-center justify-center p-6" style={{ backgroundColor: 'var(--susy-lino-naturale)' }}>
        <div className="w-full max-w-[400px]">
          <div className="lg:hidden mb-8 text-center">
            <div className="inline-flex items-center gap-3 mb-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden"
                style={{ backgroundColor: 'var(--susy-beige-fieno)' }}
              >
                <Image src="/susy-logo.png" alt="Susy®" width={40} height={40} className="object-contain" />
              </div>
              <div className="text-left">
                <div className="text-base font-semibold font-display" style={{ color: 'var(--susy-nero)' }}>{SUSY.brand}</div>
                <div className="text-xs" style={{ color: 'var(--susy-grigio-500)' }}>{SUSY.payoff}</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-sm" style={{ borderColor: 'var(--susy-grigio-200)', borderWidth: 1 }}>
            <div className="mb-7">
              <h1 className="font-display text-2xl tracking-tight" style={{ color: 'var(--susy-nero)' }}>
                Accedi all&apos;Hub
              </h1>
              <p className="text-sm mt-1.5" style={{ color: 'var(--susy-grigio-500)' }}>
                Entra con le credenziali del tuo account Susy®.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="nome@susy.pet"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: undefined })) }}
                  required
                  autoComplete="email"
                  className={`h-11 transition-colors ${errors.email ? 'border-red-400 focus:border-red-400 focus:ring-red-400/20' : ''}`}
                />
                {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                  <button
                    type="button"
                    className="text-xs hover:underline cursor-pointer"
                    style={{ color: 'var(--susy-verde-bosco)' }}
                    tabIndex={-1}
                    onClick={() => toast.info('Reset password', { description: 'Richiedi il reset a mattia@susy.pet.' })}
                  >
                    Password dimenticata?
                  </button>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setErrors((p) => ({ ...p, password: undefined })) }}
                    required
                    autoComplete="current-password"
                    className={`h-11 pr-11 transition-colors ${errors.password ? 'border-red-400 focus:border-red-400 focus:ring-red-400/20' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors cursor-pointer"
                    style={{ color: 'var(--susy-grigio-500)' }}
                    aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 font-medium cursor-pointer transition-opacity hover:opacity-90"
                style={{ backgroundColor: 'var(--susy-verde-bosco)', color: 'var(--susy-beige-fieno)' }}
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Accesso in corso…</>
                ) : 'Accedi'}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t text-center text-xs" style={{ borderColor: 'var(--susy-grigio-200)', color: 'var(--susy-grigio-500)' }}>
              Solo per il team di {SUSY.brand}.
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
